import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { HttpService } from "../http/http";
import { IExternalMAMRepository } from "../../domain/external-mam/i-external-mam.repository";
import { ExternalMAMGatewayProvider } from "../../domain/external-mam/external-mam-gateway-provider";
import { ExternalMAMServiceProvider } from "../../domain/external-mam/external-mam-service-provider";
import { ExternalMAMGatewayProviderInfo } from "../../domain/external-mam/external-mam-gateway-provider-info";
import ExternalMAMMetadataItem from "../../domain/external-mam/external-mam-metadata-item";
import { ExternalMAMSearchRequest } from "../../domain/external-mam/external-mam-search-request";
import { ExternalMAMImportRequest } from "../../domain/external-mam/external-mam-import-request";
import { error } from "console";
import { ExternalMAMUpdateItemMetadataRequest } from "../../domain/external-mam/external-mam-update-item-metadata-request";
@Injectable()
export class ExternalMamRepositoryImpl implements IExternalMAMRepository {
  readonly controller = "externalmam";
  private _serviceProvidersCache: ExternalMAMServiceProvider[] = [];
  private _gatewayProvidersCache: ExternalMAMGatewayProviderInfo[] = [];
  constructor(private httpService: HttpService) {}

  updated: Subject<ExternalMAMGatewayProvider>;
  created: Subject<ExternalMAMGatewayProvider>;
  deleted: Subject<ExternalMAMGatewayProvider>;

  async loadGatewayProviders(): Promise<ExternalMAMGatewayProviderInfo[]> {
    if (this._gatewayProvidersCache.length === 0) {
      var result = await this.httpService.getJSON(
        `${this.controller}/get-gateway-providers`,
      );
      this._gatewayProvidersCache = result.map((x) => {
        return new ExternalMAMGatewayProviderInfo(
          x.Id,
          x.ExternalMAMType,
          x.CreatedDate,
          x.Name,
          x.IsActive,
        );
      });
    }
    return Promise.resolve(this._gatewayProvidersCache);
  }
  async loadServiceProviders(): Promise<ExternalMAMServiceProvider[]> {
    if (this._serviceProvidersCache.length === 0) {
      var result = await this.httpService.getJSON(
        `${this.controller}/get-service-providers`,
      );
      this._serviceProvidersCache = result.map((x) => {
        return new ExternalMAMServiceProvider(
          x.provider_id,
          x.provider_name,
          x.provider_logo,
          x.provider_description,
          x.provider_general_fields,
        );
      });
    }
    return Promise.resolve(this._serviceProvidersCache);
  }

  async create(gateway: ExternalMAMGatewayProvider): Promise<string> {
    try {
      var result = await this.httpService.post(
        `${this.controller}/create`,
        gateway,
      );
      if (result) {
        this._gatewayProvidersCache = [];
        this.loadGatewayProviders();
        return result;
      }
    } catch {}
    return null;
  }
  async update(gateway: ExternalMAMGatewayProvider): Promise<boolean> {
    let response = await this.httpService.post(
      `${this.controller}/update`,
      gateway,
    );
    if (response) {
      let index = this._gatewayProvidersCache.findIndex(
        (x) => x.id == gateway.id,
      );
      if (index > -1) {
        this._gatewayProvidersCache[index].name = gateway.getName();
        this._gatewayProvidersCache[index].isActive = gateway.isActive;
      }
      return true;
    }
    return false;
  }
  delete(gatewayProviderUid: string): Promise<void> {
    throw new Error("Method not implemented.");
  }

  async getGatewayProviderById(
    id: string,
  ): Promise<[ExternalMAMGatewayProvider, Array<ExternalMAMMetadataItem>]> {
    var response = await this.httpService.getJSON(`${this.controller}/${id}`);
    var provider = new ExternalMAMGatewayProvider(
      response.message.provider.Id,
      response.message.provider.ExternalMAMType,
      response.message.provider.TenantId,
      response.message.provider.CreatedDate,
      response.message.provider.AdditionalData,
      response.message.provider.IsActive,
    );
    var metadata = this.mapMetadataArray(response.message.metadata);
    return [provider, metadata];
  }

  async getServiceProviderById(
    id: number,
  ): Promise<ExternalMAMServiceProvider> {
    var providers = this.loadServiceProviders();
    return (await providers).find((x) => x.id == id);
  }
  async getServiceProviderByName(
    name: string,
  ): Promise<ExternalMAMServiceProvider> {
    var providers = this.loadServiceProviders();
    return (await providers).find(
      (x) =>
        ExternalMAMServiceProvider.urlName(x.name) ===
        ExternalMAMServiceProvider.urlName(name),
    );
  }
  async getMetadata(
    provider: ExternalMAMGatewayProvider,
  ): Promise<ExternalMAMMetadataItem[]> {
    let result = await this.httpService.post(
      `${this.controller}/get-metadata`,
      provider,
    );
    return this.mapMetadataArray(result);
  }
  async testConnection(gateway: ExternalMAMGatewayProvider): Promise<boolean> {
    return await this.httpService.post(
      `${this.controller}/test-connection`,
      gateway,
    );
  }
  async getSearchResults(
    request: ExternalMAMSearchRequest,
  ): Promise<[number, Array<any>]> {
    var result = await this.httpService.post(
      `${this.controller}/get-search-results`,
      request,
    );
    return [result.count, result.results];
  }
  async startImport(request: ExternalMAMImportRequest): Promise<string> {
    try {
      await this.httpService.post(`${this.controller}/start-import`, request);
      return "";
    } catch (err) {
      return err.message;
    }
  }
  async updateItemMetadata(
    request: ExternalMAMUpdateItemMetadataRequest,
  ): Promise<string> {
    try {
      await this.httpService.post(
        `${this.controller}/update-item-metadata`,
        request,
      );
      return "";
    } catch (err) {
      return err.message;
    }
  }

  private mapMetadataArray(result: Array<ExternalMAMMetadataItem>) {
    var mappedArray = result.map((x) => x as ExternalMAMMetadataItem);
    return mappedArray.sort(function (a, b) {
      // sort by ABC
      var textA = a.displayName.toUpperCase();
      var textB = b.displayName.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }
}
